/**
 * Client-side image compression for card storage.
 *
 * Phone cameras produce 5–15 MB JPEGs. Storing them inline as base64 blows up
 * the card save request body past the proxy's body-size limit. This module
 * downscales and JPEG-compresses images at attach time so a typical phone photo
 * lands at ~200–400 KB before base64 encoding — well within the 8 MB body limit
 * even when multiple images are on one card.
 *
 * Mirrors the EXIF-orientation-correct approach from ocrPrepare.ts: decoding
 * via an <img> element causes the browser to apply EXIF rotation before
 * drawImage, so the stored image is always upright regardless of camera
 * orientation.
 */

/** Longest-side pixel cap. Sufficient for study-card viewing; keeps files small. */
const MAX_CARD_IMAGE_PX = 1200

/** JPEG quality (0–1). 0.82 is visually lossless at study-card sizes. */
const CARD_IMAGE_JPEG_QUALITY = 0.82

/**
 * Compress a File (image) for inline base64 storage on a card side.
 *
 * - Corrects EXIF orientation via <img> decode.
 * - Downscales so the longest side is at most MAX_CARD_IMAGE_PX.
 * - Re-encodes as JPEG at CARD_IMAGE_JPEG_QUALITY.
 * - SVG is returned as-is (vector markup cannot be meaningfully rasterised
 *   at a fixed resolution and is already tiny).
 *
 * Returns a `data:image/...` URL suitable for `card_side.content`.
 */
export async function compressImageForStorage(file: File): Promise<string> {
  if (file.type === 'image/svg+xml') {
    return readFileAsDataUrl(file)
  }

  const objectUrl = URL.createObjectURL(file)
  try {
    const img = await decodeImage(objectUrl)

    const longest = Math.max(img.naturalWidth, img.naturalHeight)
    const scale = longest > MAX_CARD_IMAGE_PX ? MAX_CARD_IMAGE_PX / longest : 1
    const w = Math.round(img.naturalWidth * scale)
    const h = Math.round(img.naturalHeight * scale)

    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Could not get 2D canvas context')
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'
    ctx.drawImage(img, 0, 0, w, h)

    return canvas.toDataURL('image/jpeg', CARD_IMAGE_JPEG_QUALITY)
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

function decodeImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Could not decode image. Try a different file.'))
    img.src = src
  })
}

function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = (e) => resolve(e.target?.result as string)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}
